// Pages/Dashboard/Dashboard.jsx
import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faChartLine,
  faComments,
  faCalendarCheck,
  faUserPlus,
  faPercentage,
  faArrowUp,
  faArrowDown,
  faChevronLeft,
} from "@fortawesome/free-solid-svg-icons";
import Loader from "../../Components/Core/Loader";
import Notification from "../../Components/Core/Notification";
import DashboardCharts from "./DashboardCharts";
import {
  GetDashboardData,
  GetIncomeTrend,
} from "../../Services/DashboardService";

const Dashboard = () => {
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");
  const [selectedTab, setSelectedTab] = useState("today");
  const [incomeGranularity, setIncomeGranularity] = useState("month");
  const [incomeTrend, setIncomeTrend] = useState([]);
  const navigate = useNavigate();

  const getPeriodsForGranularity = useCallback((granularity) => {
    switch (granularity) {
      case "day":
        return 30;
      case "week":
        return 12;
      case "year":
        return 5;
      case "month":
      default:
        return 6;
    }
  }, []);

  const fetchIncomeTrend = useCallback(
    async (granularity) => {
      try {
        const periods = getPeriodsForGranularity(granularity);
        const trendData = await GetIncomeTrend(granularity, periods);
        setIncomeTrend(Array.isArray(trendData) ? trendData : []);
      } catch (error) {
        setSnackbarMessage(error?.message || "خطا در دریافت روند درآمد");
        setSnackbarSeverity("error");
        setOpenSnackbar(true);
        setIncomeTrend([]);
      }
    },
    [getPeriodsForGranularity]
  );

  const fetchDashboard = useCallback(async () => {
    try {
      setLoading(true);
      const dashboardData = await GetDashboardData();
      setDashboard(dashboardData);
      await fetchIncomeTrend("month");
    } catch (error) {
      setSnackbarMessage(error?.message || "خطا در دریافت اطلاعات داشبورد");
      setSnackbarSeverity("error");
      setOpenSnackbar(true);
    } finally {
      setLoading(false);
    }
  }, [fetchIncomeTrend]);

  useEffect(() => {
    document.title = "داشبورد";
    fetchDashboard();
  }, [fetchDashboard]);

  const handleIncomeGranularityChange = async (granularity) => {
    if (granularity === incomeGranularity) return;
    setIncomeGranularity(granularity);
    await fetchIncomeTrend(granularity);
  };

  const formatCurrency = (amount) => {
    if (!amount) return "۰";
    return new Intl.NumberFormat("fa-IR").format(Math.round(amount));
  };

  const formatNumber = (num) => {
    if (!num) return "۰";
    return new Intl.NumberFormat("fa-IR").format(num);
  };

  // Custom Tooltip for Charts
  const CustomTooltip = ({ active, payload, label, isCurrency }) => {
    if (active && payload && payload.length) {
      return (
        <div className="custom-tooltip">
          <p className="tooltip-label">{label}</p>
          <p className="tooltip-value">
            {isCurrency
              ? `${formatCurrency(payload[0].value)} تومان`
              : formatNumber(payload[0].value)}
          </p>
        </div>
      );
    }
    return null;
  };

  const StatCard = ({ title, value, change, icon, isPositive, isCurrency }) => (
    <div className="stat-card">
      <div className="stat-content">
        <div className="stat-header">
          <span className="stat-title">{title}</span>
          <div className="stat-icon">
            <FontAwesomeIcon icon={icon} />
          </div>
        </div>
        <div className="stat-value">
          {isCurrency ? `${formatCurrency(value)}` : value}
          {isCurrency && <span className="currency-label"> تومان</span>}
        </div>
        <div className={`stat-change ${isPositive ? "positive" : "negative"}`}>
          <FontAwesomeIcon icon={isPositive ? faArrowUp : faArrowDown} />
          <span>
            {change === 0
              ? "بدون تغییر"
              : `${formatNumber(Math.abs(change))}٪ ${
                  isPositive ? "افزایش" : "کاهش"
                } نسبت به ماه قبل`}
          </span>
        </div>
      </div>
    </div>
  );

  if (loading || !dashboard) {
    return <Loader />;
  }

  const reservations =
    selectedTab === "today"
      ? dashboard.todayReservations
      : dashboard.tomorrowReservations;

  return (
    <>
      <style jsx>{`
        .dashboard-page {
          min-height: 100vh;
          background: linear-gradient(135deg, #f5f7fa 0%, #f0f2f5 100%);
          font-family: "MyPersianFont", "iran-sans", sans-serif;
          padding: 20px;
          direction: rtl;
          overflow-x: hidden; /* ✅ جلوگیری از scroll افقی */
        }

        .container {
          max-width: 1400px;
          margin: 0 auto;
          width: 100%; /* ✅ اضافه شد */
        }

        /* ============================================ */
        /* Header */
        /* ============================================ */

        .dashboard-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          margin-bottom: 32px;
          padding-bottom: 20px;
          border-bottom: 2px solid rgba(124, 200, 144, 0.2);
        }

        .dashboard-title {
          font-size: 32px;
          font-weight: 700;
          color: #053251;
          margin: 0 0 8px 0;
          font-family: "MyPersianFont", "iran-sans", sans-serif;
        }

        .dashboard-subtitle {
          font-size: 16px;
          color: #6c757d;
          margin: 0;
          font-family: "MyPersianFont", "iran-sans", sans-serif;
        }

        /* ============================================ */
        /* Stats Grid */
        /* ============================================ */

        .stats-grid {
          display: grid;
          grid-template-columns: repeat(
            3,
            1fr
          ); /* ✅ تغییر یافت - دسکتاپ 3 ستون */
          gap: 20px;
          margin-bottom: 32px;
        }

        .stat-card {
          background: white;
          border-radius: 16px;
          padding: 24px;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
          transition: all 0.3s ease;
          border: 1px solid #f0f0f0;
          min-width: 0; /* ✅ اضافه شد */
        }

        .stat-card:hover {
          transform: translateY(-4px);
          box-shadow: 0 8px 24px rgba(124, 200, 144, 0.15);
          border-color: rgba(124, 200, 144, 0.3);
        }

        .stat-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 16px;
        }

        .stat-title {
          font-size: 15px;
          color: #6c757d;
          font-weight: 500;
          font-family: "MyPersianFont", "iran-sans", sans-serif;
        }

        .stat-icon {
          width: 48px;
          height: 48px;
          border-radius: 12px;
          background: linear-gradient(
            135deg,
            rgba(124, 200, 144, 0.15) 0%,
            rgba(124, 200, 144, 0.05) 100%
          );
          display: flex;
          align-items: center;
          justify-content: center;
          color: #7cc890;
          font-size: 22px;
          flex-shrink: 0; /* ✅ اضافه شد */
        }

        .stat-value {
          font-size: 32px;
          font-weight: 700;
          color: #053251;
          margin-bottom: 12px;
          font-family: "MyPersianFont", "iran-sans", sans-serif;
          line-height: 1.2;
          word-break: break-word; /* ✅ اضافه شد */
        }

        .currency-label {
          font-size: 16px;
          font-weight: 500;
          color: #6c757d;
          margin-right: 4px;
        }

        .stat-change {
          font-size: 14px;
          font-weight: 600;
          display: flex;
          align-items: center;
          gap: 6px;
          font-family: "MyPersianFont", "iran-sans", sans-serif;
        }

        .stat-change.positive {
          color: #28a745;
        }

        .stat-change.negative {
          color: #dc3545;
        }

        /* ============================================ */
        /* Charts */
        /* ============================================ */

        .charts-grid {
          display: grid;
          grid-template-columns: repeat(
            2,
            1fr
          ); /* ✅ تغییر یافت - دسکتاپ 2 ستون */
          gap: 20px;
          margin-bottom: 32px;
        }

        .chart-card {
          background: white;
          border-radius: 16px;
          padding: 28px;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
          border: 1px solid #f0f0f0;
          min-width: 0; /* ✅ اضافه شد */
          overflow: hidden; /* ✅ اضافه شد */
        }

        .chart-header {
          margin-bottom: 24px;
          padding-bottom: 16px;
          border-bottom: 2px solid #f5f7fa;
        }

        .chart-title {
          font-size: 20px;
          font-weight: 700;
          color: #053251;
          margin: 0 0 8px 0;
          font-family: "MyPersianFont", "iran-sans", sans-serif;
        }

        .chart-subtitle {
          font-size: 14px;
          color: #6c757d;
          margin: 0;
          font-family: "MyPersianFont", "iran-sans", sans-serif;
        }

        /* Custom Tooltip */
        .custom-tooltip {
          background: white;
          padding: 12px 16px;
          border-radius: 8px;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
          border: 1px solid #f0f0f0;
        }

        .tooltip-label {
          font-size: 14px;
          color: #6c757d;
          margin: 0 0 4px 0;
          font-family: "MyPersianFont", "iran-sans", sans-serif;
        }

        .tooltip-value {
          font-size: 16px;
          font-weight: 700;
          color: #053251;
          margin: 0;
          font-family: "MyPersianFont", "iran-sans", sans-serif;
        }

        /* ============================================ */
        /* Lists */
        /* ============================================ */

        .lists-grid {
          display: grid;
          grid-template-columns: repeat(
            2,
            1fr
          ); /* ✅ تغییر یافت - دسکتاپ 2 ستون */
          gap: 20px;
          margin-bottom: 32px;
        }

        .list-card {
          background: white;
          border-radius: 16px;
          padding: 28px;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
          border: 1px solid #f0f0f0;
          min-width: 0; /* ✅ اضافه شد */
        }

        .list-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 24px;
          padding-bottom: 16px;
          border-bottom: 2px solid #f5f7fa;
        }

        .list-title {
          font-size: 20px;
          font-weight: 700;
          color: #053251;
          margin: 0;
          font-family: "MyPersianFont", "iran-sans", sans-serif;
        }

        .list-badge {
          background: linear-gradient(135deg, #7cc890 0%, #6ab77d 100%);
          color: white;
          font-size: 13px;
          font-weight: 700;
          padding: 6px 12px;
          border-radius: 20px;
          font-family: "MyPersianFont", "iran-sans", sans-serif;
          flex-shrink: 0; /* ✅ اضافه شد */
        }

        .list-item {
          display: flex;
          align-items: center;
          gap: 14px;
          padding: 16px;
          border-radius: 12px;
          margin-bottom: 12px;
          transition: all 0.2s ease;
          cursor: pointer;
          border: 1px solid transparent;
          min-width: 0; /* ✅ اضافه شد */
        }

        .list-item:hover {
          background: #f8f9fa;
          border-color: rgba(124, 200, 144, 0.3);
          transform: translateX(-4px);
        }

        .item-avatar {
          width: 48px;
          height: 48px;
          border-radius: 50%;
          background: linear-gradient(135deg, #7cc890 0%, #6ab77d 100%);
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
          font-weight: 700;
          font-size: 18px;
          flex-shrink: 0;
          box-shadow: 0 2px 8px rgba(124, 200, 144, 0.3);
          font-family: "MyPersianFont", "iran-sans", sans-serif;
        }

        .item-avatar-secondary {
          background: linear-gradient(135deg, #0d6efd 0%, #0b5ed7 100%);
          box-shadow: 0 2px 8px rgba(13, 110, 253, 0.3);
        }

        .item-content {
          flex: 1;
          min-width: 0; /* ✅ مهم - برای جلوگیری از overflow */
        }

        .item-name {
          font-weight: 700;
          font-size: 16px;
          color: #053251;
          margin-bottom: 6px;
          font-family: "MyPersianFont", "iran-sans", sans-serif;
          overflow: hidden; /* ✅ اضافه شد */
          text-overflow: ellipsis; /* ✅ اضافه شد */
          white-space: nowrap; /* ✅ اضافه شد */
        }

        .item-description {
          font-size: 14px;
          color: #6c757d;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
          margin-bottom: 4px;
          font-family: "MyPersianFont", "iran-sans", sans-serif;
        }

        .item-time {
          font-size: 12px;
          color: #adb5bd;
          font-family: "MyPersianFont", "iran-sans", sans-serif;
        }

        .item-badge {
          background: #dc3545;
          color: white;
          font-size: 13px;
          font-weight: 700;
          padding: 6px 12px;
          border-radius: 20px;
          font-family: "MyPersianFont", "iran-sans", sans-serif;
          box-shadow: 0 2px 8px rgba(220, 53, 69, 0.3);
          flex-shrink: 0; /* ✅ اضافه شد */
        }

        .item-arrow {
          color: #adb5bd;
          font-size: 16px;
          transition: all 0.2s ease;
          flex-shrink: 0; /* ✅ اضافه شد */
        }

        .list-item:hover .item-arrow {
          color: #7cc890;
          transform: translateX(-2px);
        }

        /* ============================================ */
        /* Reservation Tabs */
        /* ============================================ */

        .reservation-tabs {
          display: flex;
          gap: 12px;
          margin-bottom: 20px;
        }

        .tab-button {
          flex: 1;
          padding: 14px;
          border: none;
          border-radius: 12px;
          background: #f8f9fa;
          color: #6c757d;
          font-size: 15px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.3s ease;
          font-family: "MyPersianFont", "iran-sans", sans-serif;
          border: 2px solid transparent;
          min-width: 0; /* ✅ اضافه شد */
        }

        .tab-button:hover {
          background: #e9ecef;
          color: #495057;
        }

        .tab-button.active {
          background: linear-gradient(135deg, #7cc890 0%, #6ab77d 100%);
          color: white;
          box-shadow: 0 4px 12px rgba(124, 200, 144, 0.3);
        }

        .income-granularity-tabs {
          display: flex;
          gap: 8px;
          flex-wrap: wrap;
          margin-top: 12px;
        }

        .income-tab-button {
          border: 1px solid #d9e6de;
          border-radius: 999px;
          background: #f8f9fa;
          color: #495057;
          padding: 8px 14px;
          font-size: 13px;
          font-weight: 600;
          font-family: "MyPersianFont", "iran-sans", sans-serif;
          cursor: pointer;
          transition: all 0.2s ease;
        }

        .income-tab-button:hover {
          background: #eef5f0;
        }

        .income-tab-button.active {
          background: #7cc890;
          color: white;
          border-color: #7cc890;
          box-shadow: 0 3px 8px rgba(124, 200, 144, 0.3);
        }

        /* ============================================ */
        /* Empty State */
        /* ============================================ */

        .empty-state {
          text-align: center;
          padding: 48px 20px;
        }

        .empty-icon {
          font-size: 48px;
          margin-bottom: 16px;
          opacity: 0.5;
        }

        .empty-text {
          font-size: 15px;
          color: #6c757d;
          margin: 0;
          font-family: "MyPersianFont", "iran-sans", sans-serif;
        }

        /* ============================================ */
        /* Responsive */
        /* ============================================ */

        /* Tablet (1024px - 768px) */
        @media (max-width: 1024px) {
          .stats-grid {
            grid-template-columns: repeat(2, 1fr); /* ✅ تبلت: 2 ستون */
          }

          .charts-grid {
            grid-template-columns: 1fr; /* ✅ تبلت: 1 ستون */
          }

          .lists-grid {
            grid-template-columns: 1fr; /* ✅ تبلت: 1 ستون */
          }
        }

        /* Mobile (768px و کمتر) */
        @media (max-width: 768px) {
          .dashboard-page {
            padding: 12px; /* ✅ کاهش padding */
          }

          .container {
            padding: 0; /* ✅ اضافه شد */
          }

          .stats-grid {
            grid-template-columns: 1fr; /* ✅ موبایل: 1 ستون */
            gap: 12px; /* ✅ کاهش gap */
          }

          .charts-grid {
            grid-template-columns: 1fr; /* ✅ موبایل: 1 ستون */
            gap: 12px; /* ✅ کاهش gap */
          }

          .lists-grid {
            grid-template-columns: 1fr; /* ✅ موبایل: 1 ستون */
            gap: 12px; /* ✅ کاهش gap */
          }

          .dashboard-header {
            flex-direction: column;
            align-items: flex-start;
            gap: 12px;
            margin-bottom: 20px;
            padding-bottom: 16px;
          }

          .dashboard-title {
            font-size: 24px;
          }

          .dashboard-subtitle {
            font-size: 14px;
          }

          .stat-card {
            padding: 16px; /* ✅ کاهش padding */
          }

          .stat-value {
            font-size: 24px; /* ✅ کاهش سایز */
          }

          .stat-icon {
            width: 40px; /* ✅ کاهش سایز */
            height: 40px;
            font-size: 18px;
          }

          .chart-card,
          .list-card {
            padding: 16px; /* ✅ کاهش padding */
          }

          .chart-title,
          .list-title {
            font-size: 18px;
          }

          .chart-subtitle {
            font-size: 13px;
          }
        }

        /* Small Mobile (480px و کمتر) */
        @media (max-width: 480px) {
          .dashboard-page {
            padding: 8px; /* ✅ کاهش بیشتر padding */
          }

          .dashboard-title {
            font-size: 20px;
          }

          .stat-value {
            font-size: 20px; /* ✅ کاهش بیشتر */
          }

          .currency-label {
            font-size: 13px;
          }

          .stat-change {
            font-size: 12px;
          }

          .item-avatar {
            width: 40px;
            height: 40px;
            font-size: 16px;
          }

          .item-name {
            font-size: 14px;
          }

          .item-description {
            font-size: 12px;
          }

          .chart-card,
          .list-card {
            padding: 12px; /* ✅ کاهش بیشتر padding */
          }

          .tab-button {
            padding: 10px;
            font-size: 14px;
          }
        }
      `}</style>
      <div className="dashboard-page">
        <div className="container">
          {/* Header */}
          <div className="dashboard-header">
            <div>
              <h1 className="dashboard-title">داشبورد</h1>
              <p className="dashboard-subtitle">خلاصه عملکرد و آمار</p>
            </div>
          </div>

          {/* KPI Cards */}
          <div className="stats-grid">
            <StatCard
              title="درآمد این ماه"
              value={dashboard.thisMonthRevenue}
              change={dashboard.revenueChangePercent}
              icon={faChartLine}
              isPositive={dashboard.revenueChangePercent >= 0}
              isCurrency={true}
            />
            <StatCard
              title="مشاوره‌ها این ماه"
              value={dashboard.thisMonthConsultations}
              change={dashboard.consultationsChangePercent}
              icon={faComments}
              isPositive={dashboard.consultationsChangePercent >= 0}
            />
            <StatCard
              title="رزروها این ماه"
              value={dashboard.thisMonthReservations}
              change={dashboard.reservationsChangePercent}
              icon={faCalendarCheck}
              isPositive={dashboard.reservationsChangePercent >= 0}
            />
            <StatCard
              title="کاربران جدید این ماه"
              value={dashboard.newUsersThisMonth}
              change={dashboard.newUsersChangePercent}
              icon={faUserPlus}
              isPositive={dashboard.newUsersChangePercent >= 0}
            />
            <StatCard
              title="ظرفیت اشغال شده"
              value={`${dashboard.capacityOccupancy} ٪`}
              change={dashboard.capacityChangePercent}
              icon={faPercentage}
              isPositive={dashboard.capacityChangePercent >= 0}
            />
          </div>

          {/* Charts */}
          <DashboardCharts
            incomeTrend={incomeTrend}
            incomeGranularity={incomeGranularity}
            handleIncomeGranularityChange={handleIncomeGranularityChange}
            weeklyActivity={dashboard.last4WeeksActivity}
            CustomTooltip={CustomTooltip}
          />

          {/* Lists */}
          <div className="lists-grid">
            {/* Unanswered Consultations */}
            <div className="list-card">
              <div className="list-header">
                <h3 className="list-title">مشاوره‌های خوانده نشده</h3>
                {dashboard.lastUnreadConsultations.length > 0 && (
                  <span className="list-badge">
                    {formatNumber(dashboard.lastUnreadConsultations.length)}
                  </span>
                )}
              </div>
              {dashboard.lastUnreadConsultations.length > 0 ? (
                dashboard.lastUnreadConsultations.map((consultation) => (
                  <div
                    key={consultation.sessionId}
                    className="list-item"
                    onClick={() =>
                      navigate(`/counseling/chat/${consultation.sessionId}`)
                    }
                  >
                    <div className="item-avatar">
                      {consultation.patientName.charAt(0)}
                    </div>
                    <div className="item-content">
                      <div className="item-name">
                        {consultation.patientName}
                      </div>
                      <div className="item-description">
                        {consultation.lastMessagePreview}
                      </div>
                      <div className="item-time">
                        {consultation.lastMessageTimeDisplay}
                      </div>
                    </div>
                    {consultation.unreadCount > 0 && (
                      <div className="item-badge">
                        {formatNumber(consultation.unreadCount)}
                      </div>
                    )}
                    <div className="item-arrow">
                      <FontAwesomeIcon icon={faChevronLeft} />
                    </div>
                  </div>
                ))
              ) : (
                <div className="empty-state">
                  <div className="empty-icon">✓</div>
                  <p className="empty-text">همه مشاوره‌ها پاسخ داده شده‌اند</p>
                </div>
              )}
            </div>

            {/* Reservations */}
            <div className="list-card">
              <div className="list-header">
                <h3 className="list-title">نوبت‌های پیش‌رو</h3>
                {reservations.length > 0 && (
                  <span className="list-badge">
                    {formatNumber(reservations.length)}
                  </span>
                )}
              </div>
              <div className="reservation-tabs">
                <button
                  className={`tab-button ${
                    selectedTab === "today" ? "active" : ""
                  }`}
                  onClick={() => setSelectedTab("today")}
                >
                  امروز
                </button>
                <button
                  className={`tab-button ${
                    selectedTab === "tomorrow" ? "active" : ""
                  }`}
                  onClick={() => setSelectedTab("tomorrow")}
                >
                  فردا
                </button>
              </div>
              {reservations.length > 0 ? (
                reservations.map((reservation) => (
                  <div key={reservation.reserveId} className="list-item">
                    <div className="item-avatar item-avatar-secondary">
                      {reservation.patientName.charAt(0)}
                    </div>
                    <div className="item-content">
                      <div className="item-name">{reservation.patientName}</div>
                      <div className="item-description">
                        ساعت {reservation.reservationTime}
                      </div>
                    </div>
                    <div className="item-arrow">
                      <FontAwesomeIcon icon={faChevronLeft} />
                    </div>
                  </div>
                ))
              ) : (
                <div className="empty-state">
                  <div className="empty-icon">📅</div>
                  <p className="empty-text">
                    هیچ نوبتی برای {selectedTab === "today" ? "امروز" : "فردا"}{" "}
                    ثبت نشده است
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>

        <Notification
          openSnackbar={openSnackbar}
          snackbarMessage={snackbarMessage}
          snackbarSeverity={snackbarSeverity}
          setOpenSnackbar={setOpenSnackbar}
        />
      </div>
    </>
  );
};

export default Dashboard;
